import * as XLSX from "xlsx"
import type {
  FboTargetColumnMapping,
  FboTargetRowCondition,
  FboTargetSheetMapping,
  TargetSheetSpec,
} from "@/types/target-mapping"

/**
 * Reads a value from an FBO-shaped row by dot-notation path.
 * Array notation "items[].x" resolves to the first element's x (cells are scalar).
 */
export function getValueAtPath(row: Record<string, unknown>, path: string): unknown {
  const segments = path.split(".")
  let current: unknown = row

  for (const segment of segments) {
    if (current === null || current === undefined) return undefined

    const arrayMatch = segment.match(/^(.+?)\[(\d*)\]$/)
    if (arrayMatch) {
      const [, property, index] = arrayMatch
      const value = (current as Record<string, unknown>)[property]
      if (!Array.isArray(value)) return undefined
      current = value[index === "" ? 0 : Number(index)]
    } else {
      current = (current as Record<string, unknown>)[segment]
    }
  }

  return current
}

/**
 * Translates one FBO-shaped row into a sheet row keyed by target column header,
 * applying value mappings and default values.
 */
export function translateRow(
  fboRow: Record<string, unknown>,
  columnMappings: FboTargetColumnMapping[]
): Record<string, unknown> {
  const result: Record<string, unknown> = {}

  for (const mapping of columnMappings) {
    let value = mapping.sourceFboField ? getValueAtPath(fboRow, mapping.sourceFboField) : undefined

    if (value !== undefined && value !== null && mapping.valueMappings?.length) {
      const match = mapping.valueMappings.find((vm) => String(vm.sourceValue) === String(value))
      value = match ? match.destinationValue : value
    }

    if ((value === undefined || value === null || value === "") && mapping.defaultValue != null) {
      value = mapping.defaultValue
    }

    if (value !== undefined && value !== null) {
      result[mapping.targetColumn] = value
    }
  }

  return result
}

/**
 * Evaluates a sheet's row conditions against an FBO-shaped row (all must match).
 * eq/ne compare as strings; empty/notEmpty treat null, undefined, and "" as empty.
 */
export function rowMatchesConditions(
  fboRow: Record<string, unknown>,
  conditions: FboTargetRowCondition[] | null | undefined
): boolean {
  if (!conditions || conditions.length === 0) return true

  return conditions.every((condition) => {
    const value = getValueAtPath(fboRow, condition.sourceFboField)
    const isEmpty = value === null || value === undefined || value === ""

    switch (condition.operator) {
      case "empty":
        return isEmpty
      case "notEmpty":
        return !isEmpty
      case "eq":
        return !isEmpty && String(value) === String(condition.value ?? "")
      case "ne":
        return isEmpty || String(value) !== String(condition.value ?? "")
      default:
        return true
    }
  })
}

export interface SheetData {
  sheetName: string
  rows: Record<string, unknown>[]
}

/**
 * Builds the Onboarding Import workbook: one sheet per provided section, header row
 * from the target spec's exact column names (the import parser matches headers by name),
 * data rows below. Sheets are written in spec order.
 */
export function buildOnboardingWorkbook(sheets: SheetData[], spec: TargetSheetSpec[]): XLSX.WorkBook {
  const workbook = XLSX.utils.book_new()

  for (const sheetSpec of spec) {
    const data = sheets.find((s) => s.sheetName === sheetSpec.sheetName)
    if (!data) continue

    const aoa: unknown[][] = [
      [...sheetSpec.columns],
      ...data.rows.map((row) => sheetSpec.columns.map((column) => row[column] ?? "")),
    ]

    const worksheet = XLSX.utils.aoa_to_sheet(aoa)
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetSpec.sheetName)
  }

  return workbook
}

/**
 * Returns the sheets of a target mapping configuration that are generated from ATS
 * data (have a source mapping) in target-spec order
 */
export function getGeneratableSheets(
  sheets: FboTargetSheetMapping[],
  spec: TargetSheetSpec[]
): FboTargetSheetMapping[] {
  const order = new Map(spec.map((s, index) => [s.sheetName, index]))
  return sheets
    .filter((s) => s.sourceMappingName && (s.columnMappings?.length ?? 0) > 0)
    .sort((a, b) => (order.get(a.sheetName) ?? 99) - (order.get(b.sheetName) ?? 99))
}
